import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { Box, Button, Card, Chip, Switch, TextField, Typography } from "@mui/material";

const GAME_FIELDS = {
    baccarat: { enabled: "auto_be_baccarat", rate: "win_rate_baccarat", label: "Baccarat" },
    xocdia: { enabled: "auto_be_xocdia", rate: "win_rate_xocdia", label: "Xóc Đĩa" },
    rongho: { enabled: "auto_be_rongho", rate: "win_rate_rongho", label: "Rồng Hổ" },
    baucua: { enabled: "auto_be_baucua", rate: "win_rate_baucua", label: "Bầu Cua" },
    xoso: { enabled: "auto_be_xoso", rate: "win_rate_xoso", label: "Xổ Số" }
};

const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("user") || ""}` } });

const parseBool = (val, defaultVal = true) => {
    if (val === undefined || val === null) return defaultVal;
    if (typeof val === "boolean") return val;
    if (typeof val === "number") return val === 1;
    if (typeof val === "string") return val.toLowerCase() === "true" || val === "1";
    return Boolean(val);
};

export default function BlockchainAutoControl({ game }) {
    const api = process.env.REACT_APP_API_URL || "";
    const fields = GAME_FIELDS[game];
    const [enabled, setEnabled] = useState(true);
    const [rate, setRate] = useState(80);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!fields) return;
        setLoading(true);
        try {
            const response = await axios.get(`${api}/setting/get`, authConfig());
            const value = Array.isArray(response.data?.data) ? response.data.data[0] : response.data?.data;
            if (value) {
                const rawVal = value[fields.enabled] !== undefined ? value[fields.enabled] : value.auto_be;
                setEnabled(parseBool(rawVal, true));
                setRate(Number(value[fields.rate] ?? value.win_rate_sys ?? 80));
            }
        } catch (error) {
            swal("Không thể tải cấu hình", error.response?.data?.message || "Vui lòng thử lại.", "error");
        } finally {
            setLoading(false);
        }
    }, [api, fields]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        const normalizedRate = Number(rate);
        if (!Number.isFinite(normalizedRate) || normalizedRate < 0 || normalizedRate > 100) {
            swal("Dữ liệu không hợp lệ", "Tỉ lệ người chơi thắng phải từ 0 đến 100%.", "warning");
            return;
        }
        setSaving(true);
        try {
            const response = await axios.put(`${api}/setting/update`, {
                [fields.enabled]: Boolean(enabled),
                [fields.rate]: normalizedRate
            }, authConfig());
            if (response.data?.success === false) throw new Error(response.data.message);
            swal("Thành công", `Đã lưu cấu hình bẻ cầu ${fields.label}.`, "success");
            await load();
        } catch (error) {
            swal("Không thể cập nhật", error.response?.data?.message || error.message || "Vui lòng thử lại.", "error");
            await load();
        } finally {
            setSaving(false);
        }
    };

    if (!fields) return null;

    return <Card sx={{ mb: 2.5, p: { xs: 1.8, sm: 2.2 }, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)" }}>
        <Box sx={{ display: "flex", alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
            <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ color: "#0f172a", fontWeight: 500, fontSize: 15 }}>Bẻ cầu tự động</Typography>
                    <Chip size="small" color={enabled ? "success" : "default"} label={loading ? "Đang tải" : enabled ? "Đang bật" : "Đang tắt"} sx={{ fontWeight: 400, borderRadius: "6px" }} />
                </Box>
                <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: 13, fontWeight: 300 }}>Cấu hình riêng cho {fields.label}; không làm thay đổi các game Blockchain còn lại.</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                <Box 
                    onClick={() => !loading && !saving && setEnabled((prev) => !prev)}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", userSelect: "none" }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 400, color: "#475569" }}>Tự động</Typography>
                    <Switch disabled={loading || saving} checked={Boolean(enabled)} color="primary" />
                </Box>
                <Box className="classic-auto-rate" sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 400, color: "#475569", whiteSpace: "nowrap" }}>Tỉ lệ bẻ cược</Typography>
                    <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={rate}
                        disabled={loading || saving}
                        onChange={(event) => setRate(event.target.value)}
                        style={{
                            width: "56px",
                            height: "32px",
                            padding: "0 4px",
                            textAlign: "center",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#0f172a",
                            background: "#ffffff",
                            border: "1px solid #cbd5e1",
                            borderRadius: "6px",
                            outline: "none",
                            boxSizing: "border-box"
                        }}
                    />
                </Box>
                <Button variant="contained" color="success" disabled={loading || saving} onClick={save} sx={{ minHeight: 38, textTransform: "none", fontWeight: 400, borderRadius: "8px", px: 2 }}>{saving ? "Đang lưu..." : "Cập nhật"}</Button>
            </Box>
        </Box>
    </Card>;
}
